import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import Link from 'next/link'

import { CardStrip } from '@/components/about/card-strip'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { useReveal } from '@/hooks/use-reveal'

import { HAIRLINE, HAIRLINE_SOFT, INK_BODY, INK_FAINT, INK_MUTED } from '@/constants/riffle-ui'

const principles: { index: string; title: string; body: string }[] = [
    {
        index: '01',
        title: 'Photograph it once.',
        body: 'One sitting per object, on one sweep, under one key light. If a picture needs a different room to work, the object is wrong, not the room.'
    },
    {
        index: '02',
        title: 'Nothing decorative.',
        body: 'No props, no gradient standing in for a set, no type doing an impression of type. What is left is the object, its name, and what it costs.'
    },
    {
        index: '03',
        title: 'Motion is a material.',
        body: 'The deck moves the way a physical thing moves: sprung, weighted, reversible. Every formation plays backwards exactly as it played forwards.'
    }
]

const counts: { value: string; label: string }[] = [
    { value: '22', label: 'Objects' },
    { value: '06', label: 'Formations' },
    { value: '01', label: 'Sweep' },
    { value: '2026', label: 'Edition' }
]

const eyebrow = {
    fontSize: 11,
    letterSpacing: '0.32em',
    color: INK_MUTED
} as const

const About: FC = (): ReactNode => {
    const rootRef = useRef<HTMLDivElement>(null)

    useReveal(rootRef)

    return (
        <>
            <Seo
                title='About'
                description='Riffle is a studio that photographs objects the way a label photographs a sleeve — once, properly, and always as part of a set.'
            />

            <Layout>
                <div ref={rootRef} className='riffle-reveal'>
                    {/* ─── Hero — a stacked masthead: the title carries the page ── */}
                    <section
                        id='about-hero'
                        data-group
                        className='mx-auto flex w-full max-w-6xl flex-col justify-center px-6 sm:px-12'
                        style={{ minHeight: 'clamp(500px, 82vh, 900px)' }}
                    >
                        <div data-rev data-delay='0.15' className='font-mono uppercase' style={eyebrow}>
                            Riffle — Nº01 / About
                        </div>

                        <h1
                            className='font-playfair mt-8 sm:mt-10'
                            style={{
                                fontSize: 'clamp(2.9rem, 10.5vw, 8.5rem)',
                                lineHeight: 1,
                                letterSpacing: '-0.03em'
                            }}
                        >
                            {['One deck,', 'dealt twenty-two', 'ways.'].map((line, i) => (
                                <span
                                    key={line}
                                    className='block overflow-hidden'
                                    // the mask must clear the descenders; the negative top
                                    // pulls the leading back in without clipping them
                                    style={{ marginTop: i === 0 ? 0 : '-0.14em' }}
                                >
                                    <span
                                        data-title
                                        data-delay={`${0.2 + i * 0.1}`}
                                        className='inline-block'
                                        style={{ paddingBottom: '0.14em', willChange: 'transform' }}
                                    >
                                        {line}
                                    </span>
                                </span>
                            ))}
                        </h1>

                        <div data-rev className='mt-12 sm:mt-16' style={{ height: 1, background: HAIRLINE }} />

                        <div className='grid gap-10 pt-8 sm:grid-cols-[minmax(0,1fr)_minmax(0,0.72fr)] sm:gap-16'>
                            <p
                                data-rev
                                className='text-[15px] leading-[1.7] sm:text-[16.5px] sm:leading-[1.75]'
                                style={{ maxWidth: 560, color: INK_BODY }}
                            >
                                Riffle photographs objects the way a record label photographs a
                                sleeve: once, properly, and always as part of a set. The 2026
                                Library is twenty-two of them, lit on the same near-black sweep and
                                graded on the same curve.
                            </p>

                            <dl data-rev className='grid grid-cols-2 gap-x-8 gap-y-7 self-end'>
                                {[
                                    { label: 'Edition', value: '2026' },
                                    { label: 'Objects', value: 'Twenty-two' },
                                    { label: 'Sweep', value: 'One' },
                                    { label: 'Studio', value: 'London E9' }
                                ].map((item) => (
                                    <div key={item.label} className='flex flex-col-reverse gap-2'>
                                        <dt
                                            className='font-mono uppercase'
                                            style={{ fontSize: 9.5, letterSpacing: '0.26em', color: INK_FAINT }}
                                        >
                                            {item.label}
                                        </dt>

                                        <dd
                                            className='font-mono uppercase'
                                            style={{ fontSize: 12, letterSpacing: '0.14em', margin: 0 }}
                                        >
                                            {item.value}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    </section>

                    {/* ─── The set ───────────────────────────────── */}
                    <section
                        id='the-set'
                        className='mx-auto w-full max-w-6xl px-6 pb-20 pt-6 sm:px-12 sm:pb-28 sm:pt-10'
                    >
                        <div data-group className='mb-10 sm:mb-14'>
                            <div style={{ height: 1, background: HAIRLINE }} />

                            <div className='flex flex-col gap-4 pt-8 sm:flex-row sm:items-end sm:justify-between sm:gap-10'>
                                <div>
                                    <div data-rev className='font-mono uppercase' style={eyebrow}>
                                        The set
                                    </div>

                                    <h2
                                        className='font-playfair mt-3 overflow-hidden'
                                        style={{
                                            fontSize: 'clamp(1.75rem, 4.4vw, 3.25rem)',
                                            lineHeight: 1.02,
                                            letterSpacing: '-0.015em'
                                        }}
                                    >
                                        <span
                                            data-title
                                            className='inline-block'
                                            style={{ paddingBottom: '0.08em', willChange: 'transform' }}
                                        >
                                            Twenty-two objects, one light.
                                        </span>
                                    </h2>
                                </div>

                                <p
                                    data-rev
                                    className='text-[13.5px] leading-[1.7] sm:text-right'
                                    style={{ maxWidth: 320, color: INK_MUTED }}
                                >
                                    Every frame is square, shot on the same sweep and graded with one
                                    shared curve. Nothing is retouched to stand out.
                                </p>
                            </div>
                        </div>

                        <CardStrip />
                    </section>

                    {/* ─── Principles ────────────────────────────── */}
                    <section
                        id='principles'
                        data-group
                        className='mx-auto w-full max-w-6xl px-6 pb-20 sm:px-12 sm:pb-28'
                    >
                        <div style={{ height: 1, background: HAIRLINE }} />

                        <div data-rev className='font-mono pt-8 uppercase' style={eyebrow}>
                            House rules
                        </div>

                        <div className='mt-10 grid gap-12 sm:mt-14 sm:grid-cols-3 sm:gap-10'>
                            {principles.map((item) => (
                                <article key={item.index} data-rev>
                                    <div
                                        className='font-mono uppercase'
                                        style={{ fontSize: 10, letterSpacing: '0.26em', color: INK_FAINT }}
                                    >
                                        {item.index}
                                    </div>

                                    <h3
                                        className='font-playfair mt-4'
                                        style={{
                                            fontSize: 'clamp(1.375rem, 2.4vw, 1.75rem)',
                                            lineHeight: 1.14,
                                            letterSpacing: '-0.012em'
                                        }}
                                    >
                                        {item.title}
                                    </h3>

                                    <p
                                        className='mt-4 text-[13.5px] leading-[1.7]'
                                        style={{ color: INK_MUTED }}
                                    >
                                        {item.body}
                                    </p>
                                </article>
                            ))}
                        </div>
                    </section>

                    {/* ─── Counts ────────────────────────────────── */}
                    <section
                        id='counts'
                        data-group
                        className='mx-auto w-full max-w-6xl px-6 pb-20 sm:px-12 sm:pb-28'
                    >
                        <div style={{ height: 1, background: HAIRLINE }} />

                        {/* each pair is reversed so the numeral reads first while
                            the markup keeps the required dt → dd order */}
                        <dl className='grid grid-cols-2 gap-x-8 gap-y-10 pt-10 sm:grid-cols-4 sm:pt-12'>
                            {counts.map((item) => (
                                <div key={item.label} data-rev className='flex flex-col-reverse gap-3'>
                                    <dt
                                        className='font-mono uppercase'
                                        style={{ fontSize: 10, letterSpacing: '0.26em', color: INK_FAINT }}
                                    >
                                        {item.label}
                                    </dt>

                                    <dd
                                        className='font-playfair tracking-tight'
                                        style={{
                                            fontSize: 'clamp(2.25rem, 5vw, 3.5rem)',
                                            lineHeight: 1,
                                            margin: 0
                                        }}
                                    >
                                        {item.value}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </section>

                    {/* ─── Studio ────────────────────────────────── */}
                    <section
                        id='studio'
                        data-group
                        className='mx-auto w-full max-w-6xl px-6 pb-20 sm:px-12 sm:pb-28'
                    >
                        <div style={{ height: 1, background: HAIRLINE }} />

                        <div className='grid gap-8 pt-8 sm:grid-cols-[minmax(0,0.32fr)_minmax(0,1fr)] sm:gap-12'>
                            <div data-rev className='font-mono uppercase' style={eyebrow}>
                                Studio
                            </div>

                            <div>
                                <p
                                    data-rev
                                    className='text-[15px] leading-[1.75] sm:text-[16.5px]'
                                    style={{ maxWidth: 620, color: INK_BODY }}
                                >
                                    Riffle started in 2019 as a shelf of things we kept
                                    re-photographing for other people. The brief was always the same
                                    and always ignored: keep the set together. Eventually we stopped
                                    asking and started keeping our own.
                                </p>

                                <p
                                    data-rev
                                    className='mt-6 text-[13.5px] leading-[1.7] sm:text-[14.5px]'
                                    style={{ maxWidth: 620, color: INK_MUTED }}
                                >
                                    Today it is four people, one sweep, and a standing rule that
                                    nothing enters the library until it can be shot in a single
                                    afternoon. What survives that gets a number. What does not goes
                                    back on the shelf.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* ─── Close ─────────────────────────────────── */}
                    <section
                        id='about-contact'
                        data-group
                        className='mx-auto w-full max-w-6xl px-6 pb-4 sm:px-12'
                    >
                        <div style={{ height: 1, background: HAIRLINE_SOFT }} />

                        <div className='flex flex-col gap-8 pt-14 sm:flex-row sm:items-end sm:justify-between sm:gap-10 sm:pt-16'>
                            <h2
                                className='font-playfair overflow-hidden'
                                style={{
                                    fontSize: 'clamp(2rem, 5.5vw, 4rem)',
                                    lineHeight: 1,
                                    letterSpacing: '-0.015em'
                                }}
                            >
                                <span
                                    data-title
                                    className='inline-block'
                                    style={{ paddingBottom: '0.08em', willChange: 'transform' }}
                                >
                                    Come and see the set.
                                </span>
                            </h2>

                            <div data-rev className='sm:shrink-0'>
                                <Link
                                    href='/contact'
                                    className='riffle-order inline-flex items-center rounded-full px-7 py-3.5 text-[13px] font-semibold tracking-wide sm:px-8 sm:py-4'
                                    style={{
                                        background: 'rgba(255,255,255,0.97)',
                                        color: '#0a0a0c',
                                        boxShadow: '0 14px 36px -12px rgba(0,0,0,0.65)'
                                    }}
                                >
                                    <span
                                        className='riffle-order-sheen pointer-events-none absolute inset-0'
                                        aria-hidden
                                        style={{
                                            background:
                                                'linear-gradient(115deg, transparent 35%, rgba(255,255,255,0.55) 50%, transparent 65%)'
                                        }}
                                    />

                                    <span style={{ position: 'relative' }}>
                                        Get in touch →
                                    </span>
                                </Link>
                            </div>
                        </div>
                    </section>
                </div>
            </Layout>
        </>
    )
}

export default About
